import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, useWindowDimensions } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { useAuthStore } from '../store/useAuthStore';
import { useActivityStore } from '../store/useActivityStore';
import { useStrokeStore } from '../store/useStrokeStore';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { TaskQueueService } from '../services/TaskQueueService';
import { ChildFriendlyBackground } from '../components/ChildFriendlyBackground';
import { FriendlyButton } from '../components/FriendlyButton';
import { AudioPromptButton } from '../components/AudioPromptButton';

type Props = StackScreenProps<RootStackParamList, 'StudentSuggestion'>;

export function StudentSuggestionScreen({ navigation, route }: Props) {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const { classId, serverUrl, typedName, suggestions } = route.params;
    const [joinError, setJoinError] = useState<string | null>(null);

    const joinStudent = async (studentId: string, name: string, needsEnrollment: boolean) => {
        setJoinError(null);
        useActivityStore.getState().resetActivities();
        useStrokeStore.getState().clearStrokes();
        try {
            if (needsEnrollment && !await TaskQueueService.enrollExistingStudent(studentId, classId, serverUrl)) throw new Error('Unable to join class');
            const tasks = await TaskQueueService.fetchPendingTasks(studentId, serverUrl);
            useAuthStore.getState().joinClass(classId, name, studentId, serverUrl, 'KS1');
            useActivityStore.getState().setStudentId(studentId);
            useActivityStore.getState().loadPreFetchedTasks(tasks);
            StrokeSyncService.startSync();
            navigation.replace('TaskQueue');
        } catch (error) {
            console.error('[StudentSuggestionScreen] Unable to join class:', error);
            setJoinError("Let's try again! Check your connection and choose a name.");
        }
    };

    const handleSelectSuggestion = (studentId: string, name: string) => joinStudent(studentId, name, true);
    const handleNewStudent = async () => {
        try {
            const result = await TaskQueueService.resolveOrCreateStudent(typedName, classId, serverUrl);
            if (!result) return setJoinError("Let's try again! Check your connection and choose a name.");
            await joinStudent(result.studentId, typedName, false);
        } catch (error) {
            console.error('[StudentSuggestionScreen] Unable to create student:', error);
            setJoinError("Let's try again! Check your connection and choose a name.");
        }
    };

    return (
        <ChildFriendlyBackground variant="light">
            <View style={styles.centerWrapper}>
                <View style={[styles.card, isLandscape && styles.landscapeCard]}>
                <Text style={[styles.title, isLandscape && styles.landscapeTitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Is this you?</Text>
                <Text style={[styles.subtitle, isLandscape && styles.landscapeSubtitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    You typed: <Text style={styles.bold} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{typedName}</Text>
                </Text>

                <AudioPromptButton text="Is this you? Tap your name." autoPlay={false} />

                <View style={styles.suggestionsList}>
                    {suggestions.map((s) => (
                        <TouchableOpacity
                            key={s.id}
                            style={styles.suggestionBtn}
                            onPress={() => handleSelectSuggestion(s.id, s.name)}
                            activeOpacity={0.75}
                            accessibilityLabel={`Select ${s.name}`}
                            accessibilityRole="button"
                            accessibilityHint={`Join the class as ${s.name}`}
                        >
                            <Text style={styles.suggestionName} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{s.name}</Text>
                            <Text style={styles.suggestionClass} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{s.className}</Text>
                        </TouchableOpacity>
                    ))}
                </View>

                <FriendlyButton
                    variant="secondary"
                    size="medium"
                    onPress={handleNewStudent}
                    accessibilityLabel="No, I'm new"
                    accessibilityHint="Create a new student with the name you typed"
                >
                    No — I'm new
                </FriendlyButton>

                {joinError ? <Text style={styles.joinError} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>{joinError}</Text> : null}

                <TouchableOpacity
                    style={[styles.backBtn, isLandscape && styles.landscapeButton]}
                    onPress={() => navigation.goBack()}
                    accessibilityLabel="Go back"
                    accessibilityRole="button"
                    accessibilityHint="Go back to the name entry screen"
                >
                    <Text style={styles.backBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Go back</Text>
                </TouchableOpacity>
                </View>
            </View>
        </ChildFriendlyBackground>
    );
}

const styles = StyleSheet.create({
    card: { width: '90%', padding: 24, backgroundColor: '#FFFFFF', borderRadius: 24, alignItems: 'center', shadowColor: '#FAAD14', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.15, shadowRadius: 16, elevation: 6, borderWidth: 2, borderColor: '#FFF7E6' },
    landscapeCard: { padding: 16, width: '70%', maxWidth: 500 },
    title: { fontSize: 28, fontWeight: 'bold', color: '#0F172A', marginBottom: 8 },
    landscapeTitle: { fontSize: 22, marginBottom: 4 },
    subtitle: { fontSize: 14, color: '#64748B', marginBottom: 20 },
    landscapeSubtitle: { marginBottom: 12 },
    bold: { fontWeight: '700', color: '#0F172A' },
    suggestionsList: { width: '100%', marginBottom: 16 },
    suggestionBtn: {
        width: '100%', paddingVertical: 16, paddingHorizontal: 20,
        backgroundColor: '#EEF2FF', borderRadius: 12, marginBottom: 10,
        borderWidth: 2, borderColor: '#C7D2FE'
    },
    suggestionName: { fontSize: 18, fontWeight: '800', color: '#1E3A8A' },
    suggestionClass: { fontSize: 12, color: '#64748B', marginTop: 2 },
    centerWrapper: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
    landscapeButton: { height: 56, borderRadius: 16 },
    joinError: { color: '#92400E', fontSize: 16, fontWeight: '600', textAlign: 'center', marginTop: 12 },
    backBtn: {
        width: '100%', height: 56, backgroundColor: '#1e293b', borderRadius: 16,
        alignItems: 'center', justifyContent: 'center', marginTop: 12
    },
    backBtnText: { color: '#94a3b8', fontSize: 16, fontWeight: '700' }
});
